import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { ChatIcon, DeleteIcon, EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { chatTargetsPropType } from 'app/shared/components/TeamChat/hooks/useLoanChatTargets/constants';
import { getDiscussChatMenuTargets } from 'app/shared/components/TeamChat/TeamChat.discussMenu.utils';
import { ENTITY_REFERENCE_TYPE } from 'app/shared/components/TeamChat/TeamChat.entityReferences.utils';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';

import { selectEntityReferenceLabel } from 'store/loanApplications/selectors';
import { requestDiscussEntityInChatAction } from 'store/teamChat/actions';

import {
  FINANCIAL_INFO_ACTION_TYPE,
  FINANCIAL_INFO_VIEWS_PATHS,
} from '../../FinancialInfo.constants';
import { generateFinancialInfoPath } from '../../FinancialInfo.utils';

const EMPTY_CHAT_TARGETS = {};

const ActionButton = ({
  id: recordId = null,
  tablePath,
  disabled = false,
  removeText = '',
  onRemove,
  isRemoveDisabled = false,
  isHighlighted = false,
  entityType = null,
  chatTargets = EMPTY_CHAT_TARGETS,
}) => {
  const dispatch = useDispatch();
  const { applicationId, tab, entityId: borrowerId } = useParams();
  const history = useHistory();
  const { pathname: pathFrom } = useLocation();

  const entityLabel = useSelector(selectEntityReferenceLabel({ entityType, entityId: recordId }));

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const onClickEdit = useCallback(() => {
    onClearAnchorElement();

    const to = generateFinancialInfoPath({
      applicationId,
      tab,
      tablePath,
      borrowerId,
      actionType: FINANCIAL_INFO_ACTION_TYPE.EDIT,
      recordId,
      pathFrom,
    });

    history.push(to);
  }, [
    applicationId,
    tab,
    tablePath,
    borrowerId,
    pathFrom,
    history,
    onClearAnchorElement,
    recordId,
  ]);

  const onClickRemove = useCallback(() => {
    onClearAnchorElement();
    onRemove(recordId);
  }, [recordId, onRemove, onClearAnchorElement]);

  const onClickDiscuss = useCallback(
    chatId => {
      onClearAnchorElement();

      dispatch(
        requestDiscussEntityInChatAction({
          chatId,
          entityType,
          entityId: recordId,
          label: entityLabel,
        }),
      );
    },
    [dispatch, entityType, recordId, entityLabel, onClearAnchorElement],
  );

  const discussTargets = useMemo(
    () => (entityType && chatTargets ? getDiscussChatMenuTargets(chatTargets) : []),
    [entityType, chatTargets],
  );

  const items = useMemo(
    () => [
      { text: 'Edit', onClick: onClickEdit, icon: <EditPenIcon size={IconSize[20]} /> },
      ...discussTargets.map(({ chatId, text }) => ({
        text,
        onClick: () => onClickDiscuss(chatId),
        icon: <ChatIcon size={IconSize[20]} />,
      })),
      {
        text: removeText,
        onClick: onClickRemove,
        icon: <DeleteIcon size={IconSize[20]} />,
        isDisabled: isRemoveDisabled,
      },
    ],
    [onClickEdit, onClickRemove, isRemoveDisabled, removeText, discussTargets, onClickDiscuss],
  );

  return (
    <>
      <MoreButtonCDS
        disabled={disabled}
        isHighlighted={isHighlighted}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />

      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
    </>
  );
};

ActionButton.propTypes = {
  tablePath: PropTypes.oneOf(Object.values(FINANCIAL_INFO_VIEWS_PATHS)).isRequired,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  isRemoveDisabled: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  removeText: PropTypes.string,
  entityType: PropTypes.oneOf(Object.values(ENTITY_REFERENCE_TYPE)),
  chatTargets: chatTargetsPropType,
  onRemove: PropTypes.func,
};

export default ActionButton;
